import React, { useState, useMemo } from 'react';
import { useStore } from '../../store/StoreContext';
import { useConfirm } from '../../hooks/useConfirm';
import { useToast } from '../../hooks/useToast';
import { Button } from '../../components/ui';

function DataMigration() {
    // In-app confirm (QA M2 NEW-9 native-dialog sweep).
    const { confirm: confirmDialog, confirmElement } = useConfirm();
    const { clients, estimates, saveEstimate } = useStore();
    const { show: showToast } = useToast();
    const [migrating, setMigrating] = useState(false);
    const [result, setResult] = useState(null);

    const migratable = useMemo(() => {
        return clients.filter(c => {
            const hasData = c.origin_address || c.destination_address || c.move_date;
            if (!hasData) return false;
            const hasEstimate = estimates.some(e => e.client_id === c.id);
            return !hasEstimate;
        });
    }, [clients, estimates]);

    const runMigration = async () => {
        if (!(await confirmDialog({ title: `Create ${migratable.length} estimate${migratable.length === 1 ? '' : 's'} from client data?`, body: `One new estimate is created for each of the ${migratable.length} client${migratable.length === 1 ? '' : 's'} without one, using the details already on their record. Existing clients and estimates are not changed.`, confirmLabel: 'Run migration' }))) return;
        setMigrating(true);
        let created = 0, errors = 0;
        for (const c of migratable) {
            try {
                await saveEstimate({
                    client_id: c.id,
                    // 'building' replaces the old 'new' key (May 2026 rewrite).
                    status: c.move_date && new Date(c.move_date) < new Date() ? 'booked' : 'building',
                    origin_address: c.origin_address || '',
                    destination_address: c.destination_address || '',
                    additional_stops: c.additional_stops || '[]',
                    move_date: c.move_date || null,
                    walkthrough_date: c.walkthrough_date || null,
                    job_type: c.job_type || 'LOCAL',
                    packing_needed: c.packing_needed || 'none',
                    heavy_items: c.heavy_items || '',
                    from_sqft: c.from_sqft || 0,
                    from_levels: c.from_levels || 1,
                    from_stairs: c.from_stairs || false,
                    from_elevator: c.from_elevator || false,
                    from_driveway: c.from_driveway || '',
                    from_walk_distance: c.from_walk_distance || '',
                    to_sqft: c.to_sqft || 0,
                    to_levels: c.to_levels || 1,
                    to_stairs: c.to_stairs || false,
                    to_elevator: c.to_elevator || false,
                    to_driveway: c.to_driveway || '',
                    to_walk_distance: c.to_walk_distance || '',
                    crew_size_needed: c.crew_size_needed || 0,
                    trucks_needed: c.trucks_needed || '',
                    description: c.description || '',
                    internal_notes: c.internal_notes || '',
                });
                created++;
            } catch (e) {
                console.error('Migration error for client', c.name, e);
                errors++;
            }
        }
        setResult({ created, errors });
        setMigrating(false);
        showToast(`Migration complete: ${created} estimates created${errors > 0 ? `, ${errors} errors` : ''}`);
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-2">Data Migration</h2>
            <p className="text-xs text-slate-500 mb-4">Create estimates from existing client records that have move data (addresses, dates, etc.)</p>

            {migratable.length === 0 ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700">
                    <strong>All caught up!</strong> All clients with move data already have an estimate, or no clients have move data to migrate.
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                        <strong>{migratable.length} client{migratable.length !== 1 ? 's' : ''}</strong> with move data but no estimate found.
                        This will create an estimate for each, copying addresses, dates, property details, and notes.
                    </div>
                    <Button variant="primary" onClick={runMigration} loading={migrating}>
                        {`Migrate ${migratable.length} Client${migratable.length !== 1 ? 's' : ''} to Estimates`}
                    </Button>
                </div>
            )}

            {result && (
                <div className="mt-3 bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                    Created {result.created} estimate{result.created !== 1 ? 's' : ''}{result.errors > 0 ? `, ${result.errors} error${result.errors !== 1 ? 's' : ''}` : ''}
                </div>
            )}
            {confirmElement}
        </div>
    );
}

export default DataMigration;
